import axios from "axios";
import { notification } from "antd";
import { eraseCookie, readAuthCookie, readCookie } from "./CookieScript";
import Route from "./Route";
import { useNavigate } from "react-router-dom";
import { logOut } from "../services/LogOut";
import { isStoredUserVerified, setStoredUserVerified } from "../services/EmailVerified";
import { hasStoredActivePlan, setStoredActivePlan } from "../services/Plan";

axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

axios.interceptors.request.use(function (config) {
  // console.log("token",readAuthCookie("auth_token"));
  // only send a token when logged in (otherwise requests carried "Bearer null")
  const token = readAuthCookie("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});


// ----------------------------------------------------------------------
// log out the user when the token is no longer valid or the account was deactivated
// (only when logged in, so failed logins on the login page are left to the login form)
axios.interceptors.response.use(function (response) {
  return response;
}, function (error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 403 && data?.error === 'patient_not_verified') {
    // same key, so repeated attempts show one message
    notification.warning({ key: 'patient_not_verified', message: 'Patient email not verified', description: data.message });
  }
  if (readAuthCookie("auth_token")) {
    if (status === 401) {
      logOut('Your session has ended. Please log in again.')
    } else if (status === 403 && data?.error === 'account_inactive') {
      logOut(data.message)
    } else if (status === 403 && data?.error === 'plan_required' && hasStoredActivePlan()) {
      // the plan has expired (or none was chosen): reload to show "Choose your plan"
      setStoredActivePlan(false)
      window.location.assign('/')
    } else if (status === 403 && data?.error === 'email_not_verified' && isStoredUserVerified()) {
      // the email was changed and must be verified again: reload to show the "please verify" screen
      setStoredUserVerified(false)
      window.location.assign('/')
    }
  }
  return Promise.reject(error);
});

// ----------------------------------------------------------------------


// ===================================

// ===================================
// login
// ===================================

// export const userloginApi = (loginData) => {
//   return axios({
//     method: 'post',
//     headers: { 'Content-Type': 'application/json','Access-Control-Allow-Origin':'*' }, 
//     url: Route.userlogin, 
//     data: loginData
//   });
// };


export const getLoginUserDetailsApi = () => {
  return axios.post(Route.getLoginUserDetails);
};

// ===================================
// register
// ===================================
export const registerUserApi = (data) => {
  return axios.post(Route.registerUser, data);
};

// ===================================
// logout
// ===================================
export const logOutApi = () => {
  return axios.post(Route.logout);
};


// ===================================
// Plan 
// ===================================

export const listOfPlansApi = () => {
  return axios.get(Route.listOfPlans);
};


export const addPlanApi = (data) => {
  return axios.post(Route.addPlan, data);
};

export const editPlanApi = (id) => {
  return axios.get(`${Route.editPlan}/${id}/edit`)
};

export const updatePlanApi = (id, data) => {
  return axios.patch(`${Route.updatePlan}/${id}`, data)
};



// ===================================
// USER MODULE APIS 
// ===================================
export const listOfUsersApi = () => {
  return axios.get(Route.listOfUser);
};

export const addUserApi = (data) => {
  return axios.post(Route.addUser, data);
};

export const editUserApi = (id) => {
  return axios.get(`${Route.editUser}/${id}/edit`)
};

export const updateUserApi = (id, data) => {
  return axios.patch(`${Route.updateUser}/${id}`, data)
};

// email a password reset link
export const forgotPasswordApi = (email) => {
  return axios.post(Route.forgotPassword, { email }, { headers: { 'Content-Type': 'application/json' } });
};

// set a new password with the emailed token
export const resetPasswordApi = (data) => {
  return axios.post(Route.resetPassword, data, { headers: { 'Content-Type': 'application/json' } });
};

// verify an email with the token from the verification link
export const verifyEmailApi = (token) => {
  return axios.post(Route.verifyEmail, { token }, { headers: { 'Content-Type': 'application/json' } });
};

// send a new verification link to the logged-in user
export const resendVerificationEmailApi = () => {
  return axios.post(Route.resendVerificationEmail);
};

// what deleting a user would remove (clients, programs, recipes, herbs)
export const deleteUserSummaryApi = (id) => {
  return axios.get(`${Route.deleteUser}/${id}/delete-summary`)
};

// permanently delete a user and everything they own
export const deleteUserApi = (id) => {
  return axios.delete(`${Route.deleteUser}/${id}`)
};

// ===================================
// RECIPE MASTER MODULE APIS 
// ===================================

// Recipe Category 
// ------------------------
export const listOfRecipeCategoriesApi = () => {
  return axios.get(Route.listOfRecipeCategories);
};

export const addRecipeCategoryApi = (data) => {
  return axios.post(Route.addRecipeCategory, data);
};

export const editRecipeCategoryApi = (id, data) => {
  return axios.get(`${Route.editRecipeCategory}/${id}/edit`, data)
};

export const updateRecipeCategoryApi = (id, data) => {
  return axios.patch(`${Route.updateRecipeCategory}/${id}`, data)
};

// Recipe Types 
// ------------------------
export const listOfRecipeTypesApi = () => {
  return axios.get(Route.listOfRecipeTypes);
};

export const addRecipeTypeApi = (data) => {
  return axios.post(Route.addRecipeType, data);
};

export const editRecipeTypeApi = (id, data) => {
  return axios.get(`${Route.editRecipeType}/${id}/edit`, data)
};

export const updateRecipeTypeApi = (id, data) => {
  return axios.patch(`${Route.updateRecipeType}/${id}`, data)
};

// Recipe Types 
// ------------------------
export const listOfRecipePhenoTypesApi = () => {
  return axios.get(Route.listOfRecipePhenoTypes);
};

export const addRecipePhenoTypeApi = (data) => {
  return axios.post(Route.addRecipePhenoType, data);
};

export const editRecipePhenoTypeApi = (id, data) => {
  return axios.get(`${Route.editRecipePhenoType}/${id}/edit`, data)
};

export const updateRecipePhenoTypeApi = (id, data) => {
  return axios.patch(`${Route.updateRecipePhenoType}/${id}`, data)
};

// Recipe Meal Time 
// ------------------------
export const listOfMealTimeApi = () => {
  return axios.get(Route.listOfMealTime);
};

export const addRecipelistOfMealTimeApi = (data) => {
  return axios.post(Route.addRecipelistOfMealTime, data);
};

export const editRecipelistOfMealTimeApi = (id, data) => {
  return axios.get(`${Route.editRecipelistOfMealTime}/${id}/edit`, data)
};

export const updateRecipelistOfMealTimeApi = (id, data) => {
  return axios.patch(`${Route.updateRecipelistOfMealTime}/${id}`, data)
};

// Recipe Ingredient Form
// ------------------------
export const listOfIngredientFormApi = () => {
  return axios.get(Route.listOfIngredientForm);
};

export const addIngredientFormApi = (data) => {
  return axios.post(Route.addIngredientForm, data);
};

export const editIngredientFormApi = (id, data) => {
  return axios.get(`${Route.editIngredientForm}/${id}/edit`, data)
};

export const updateIngredientFormApi = (id, data) => {
  return axios.patch(`${Route.updateIngredientForm}/${id}`, data)
};


// Recipe Ingredient Unit list Apis
// ------------------------
export const listOfIngredientUnitApi = () => {
  return axios.get(Route.listOfIngredientUnit);
};

export const addIngredientUnitApi = (data) => {
  return axios.post(Route.addIngredientUnit, data);
};

export const editIngredientUnitApi = (id, data) => {
  return axios.get(`${Route.editIngredientUnit}/${id}/edit`, data)
};

export const updateIngredientUnitApi = (id, data) => {
  return axios.patch(`${Route.updateIngredientUnit}/${id}`, data)
};


// Recipe Tags Api
// ------------------------
export const listOfRecipeTagsApi = () => {
  return axios.get(Route.listOfRecipeTag);
};

export const addRecipeTagApi = (data) => {
  return axios.post(Route.addRecipeTag, data);
};

export const editRecipeTagApi = (id, data) => {
  return axios.get(`${Route.editRecipeTag}/${id}/edit`, data)
};

export const updateRecipeTagApi = (id, data) => {
  return axios.patch(`${Route.updateRecipeTag}/${id}`, data)
};

// Macros Micros
// ------------------------
export const listOfMacrosMicrosApi = () => {
  return axios.get(Route.listOfMacrosMicros);
};

export const addMacrosMicrosApi = (data) => {
  return axios.post(Route.addMacrosMicros, data);
};

export const editMacrosMicrosApi = (id, data) => {
  return axios.get(`${Route.editMacrosMicros}/${id}/edit`, data)
};

export const updateMacrosMicrosApi = (id, data) => {
  return axios.patch(`${Route.updateMacrosMicros}/${id}`, data)
};



// ===================================
// PATIENT MASTER MODULE APIS 
// ===================================

// Menstrual History
// ------------------------
export const listOfMenstrualHistoryApi = () => {
  return axios.get(Route.listOfMenstrualHistory);
};

export const addMenstrualHistoryApi = (data) => {
  return axios.post(Route.addMenstrualHistory, data);
};

export const editMenstrualHistoryApi = (id, data) => {
  return axios.get(`${Route.editMenstrualHistory}/${id}/edit`, data)
};

export const updateMenstrualHistoryApi = (id, data) => {
  return axios.patch(`${Route.updateMenstrualHistory}/${id}`, data)
};

export const deleteMenstrualHistoryApi = (id) => {
  return axios.delete(`${Route.deleteMenstrualHistory}/${id}`)
};

// Diseases 
// ------------------------
export const listOfDiseasesApi = () => {
  return axios.get(Route.listOfDiseases);
};

export const addDiseasesApi = (data) => {
  return axios.post(Route.addDiseases, data);
};

export const editDiseasesApi = (id, data) => {
  return axios.get(`${Route.editDiseases}/${id}/edit`, data)
};

export const updateDiseasesApi = (id, data) => {
  return axios.patch(`${Route.updateDiseases}/${id}`, data)
};

// BpMeasurement Types 
// ------------------------
export const listOfBpMeasurementApi = () => {
  return axios.get(Route.listOfBpMeasurement);
};

export const addBpMeasurementApi = (data) => {
  return axios.post(Route.addBpMeasurement, data);
};

export const editBpMeasurementApi = (id, data) => {
  return axios.get(`${Route.editBpMeasurement}/${id}/edit`, data)
};

export const updateBpMeasurementApi = (id, data) => {
  return axios.patch(`${Route.updateBpMeasurement}/${id}`, data)
};

// Routine Diet
// ------------------------
export const listOfRoutineDietApi = () => {
  return axios.get(Route.listOfRoutineDiet);
};

export const addRoutineDietApi = (data) => {
  return axios.post(Route.addRoutineDiet, data);
};

export const editRoutineDietApi = (id, data) => {
  return axios.get(`${Route.editRoutineDiet}/${id}/edit`, data)
};

export const updateRoutineDietApi = (id, data) => {
  return axios.patch(`${Route.updateRoutineDiet}/${id}`, data)
};

// Reason Of Weight Loss
// ------------------------
export const listOfWeightLossApi = () => {
  return axios.get(Route.listOfWeightLoss);
};

export const addWeightLossApi = (data) => {
  return axios.post(Route.addWeightLoss, data);
};

export const editWeightLossApi = (id, data) => {
  return axios.get(`${Route.editWeightLoss}/${id}/edit`, data)
};

export const updateWeightLossApi = (id, data) => {
  return axios.patch(`${Route.updateWeightLoss}/${id}`, data)
};

// DietTime
// ------------------------
export const listOfDietTimeApi = () => {
  return axios.get(Route.listOfDietTime);
};

export const addDietTimeApi = (data) => {
  return axios.post(Route.addDietTime, data);
};

export const editDietTimeApi = (id, data) => {
  return axios.get(`${Route.editDietTime}/${id}/edit`, data)
};

export const updateDietTimeApi = (id, data) => {
  return axios.patch(`${Route.updateDietTime}/${id}`, data)
};


// Diet Types
// ------------------------
export const listOfDietTypesApi = () => {
  return axios.get(Route.listOfDietTypes);
};

export const addDietTypesApi = (data) => {
  return axios.post(Route.addDietTypes, data);
};

export const editDietTypesApi = (id, data) => {
  return axios.get(`${Route.editDietTypes}/${id}/edit`, data)
};

export const updateDietTypesApi = (id, data) => {
  return axios.patch(`${Route.updateDietTypes}/${id}`, data)
};

// Measurements Unit
// ------------------------
export const listOfMeasurementsUnitApi = () => {
  return axios.get(Route.listOfMeasurementsUnit);
};

export const addMeasurementsUnitApi = (data) => {
  return axios.post(Route.addMeasurementsUnit, data);
};

export const editMeasurementsUnitApi = (id, data) => {
  return axios.get(`${Route.editMeasurementsUnit}/${id}/edit`, data)
};

export const updateMeasurementsUnitApi = (id, data) => {
  return axios.patch(`${Route.updateMeasurementsUnit}/${id}`, data)
};

// Question Domain
// ------------------------
export const listOfQuestionDomainApi = () => {
  return axios.get(Route.listOfQuestionDomain);
};

export const addQuestionDomainApi = (data) => {
  return axios.post(Route.addQuestionDomain, data);
};

export const editQuestionDomainApi = (id, data) => {
  return axios.get(`${Route.editQuestionDomain}/${id}/edit`, data)
};

export const updateQuestionDomainApi = (id, data) => {
  return axios.patch(`${Route.updateQuestionDomain}/${id}`, data)
};

// Herb Management
// ------------------------
export const listOfHerbManagementApi = () => {
  return axios.get(Route.listOfHerbManagement);
};

export const addHerbManagementApi = (data) => {
  return axios.post(Route.addHerbManagement, data);
};

export const editHerbManagementApi = (id, data) => {
  return axios.get(`${Route.editHerbManagement}/${id}/edit`, data)
};

export const updateHerbManagementApi = (id, data) => {
  return axios.patch(`${Route.updateHerbManagement}/${id}`, data)
};

// Phenotype Question
// ------------------------
export const listOfPhenotypeQuestionApi = () => {
  return axios.get(Route.listOfPhenotypeQuestion);
};

export const addPhenotypeQuestionApi = (data) => {
  return axios.post(Route.addPhenotypeQuestion, data);
};

export const editPhenotypeQuestionApi = (id, data) => {
  return axios.get(`${Route.editPhenotypeQuestion}/${id}/edit`, data)
};

export const updatePhenotypeQuestionApi = (id, data) => {
  return axios.patch(`${Route.updatePhenotypeQuestion}/${id}`, data)
};


// Measurements Types
// ------------------------
export const listOfMeasurementTypesApi = () => {
  return axios.get(Route.listOfMeasurementTypes);
};

export const addMeasurementTypeApi = (data) => {
  return axios.post(Route.addMeasurementType, data);
};

export const editMeasurementTypeApi = (id, data) => {
  return axios.get(`${Route.editMeasurementType}/${id}/edit`, data)
};

export const updateMeasurementTypeApi = (id, data) => {
  return axios.post(`${Route.updateMeasurementType}/${id}`, data)
};

// Focus Health Areas
// ------------------------
export const listOfFocusHealthAreaApi = () => {
  return axios.get(Route.listOfFocusHealthArea);
};

export const addFocusHealthAreaApi = (data) => {
  return axios.post(Route.addFocusHealthArea, data);
};

export const editFocusHealthAreaApi = (id, data) => {
  return axios.get(`${Route.editFocusHealthArea}/${id}/edit`, data)
};

export const updateFocusHealthAreaApi = (id, data) => {
  return axios.patch(`${Route.updateFocusHealthArea}/${id}`, data)
};

// Country
// ------------------------
export const listOfCountryApi = () => {
  return axios.get(Route.listOfCountry);
};

export const addCountryApi = (data) => {
  return axios.post(Route.addCountry, data);
};

export const editCountryApi = (id, data) => {
  return axios.get(`${Route.editCountry}/${id}/edit`, data)
};

export const updateCountryApi = (id, data) => {
  return axios.patch(`${Route.updateCountry}/${id}`, data)
};

// State
// ------------------------
export const listOfStateApi = () => {
  return axios.get(Route.listOfState);
};

export const addStateApi = (data) => {
  return axios.post(Route.addState, data);
};

export const editStateApi = (id, data) => {
  return axios.get(`${Route.editState}/${id}/edit`, data)
};

export const updateStateApi = (id, data) => {
  return axios.patch(`${Route.updateState}/${id}`, data)
};

// City
// ------------------------
export const listOfCityApi = () => {
  return axios.get(Route.listOfCity);
};

export const addCityApi = (data) => {
  return axios.post(Route.addCity, data);
};

export const editCityApi = (id, data) => {
  return axios.get(`${Route.editCity}/${id}/edit`, data)
};

export const updateCityApi = (id, data) => {
  return axios.patch(`${Route.updateCity}/${id}`, data)
};

// ========================================
// User Profile
// ------------------------
export const getUserProfileDetailsApi = (id) => {
  return axios.get(`${Route.getUserProfileDetails}/${id}/edit`);
};


// Complaints
// ------------------------
export const listOfComplaintsApi = () => {
  return axios.get(Route.listOfComplaints);
};

export const addComplaintsApi = (data) => {
  return axios.post(Route.addComplaints, data);
};

// export const editComplaintsApi = (id, data) => {
//   return axios.get(`${Route.editComplaints}/${id}/edit`, data)
// };

// export const updateComplaintsApi = (id, data) => {
//   return axios.patch(`${Route.updateComplaints}/${id}`, data)
// };


// Program
// ------------------------
export const listOfProgramApi = () => {
  return axios.get(Route.listOfProgram);
};

export const addProgramApi = (data) => {
  return axios.post(Route.addProgram, data);
};

export const editProgramApi = (id, data) => {
  return axios.get(`${Route.editProgram}/${id}/edit`, data)
};

export const updateProgramApi = (id, data) => {
  return axios.patch(`${Route.updateProgram}/${id}`, data)
};

// Ingredients Api
// ------------------------
export const listOfIngredientsApi = () => {
  return axios.get(Route.listOfIngredients);
};

export const addIngredientsApi = (data) => {
  return axios.post(Route.addIngredients, data);
};

export const editIngredientsApi = (id, data) => {
  return axios.get(`${Route.editIngredients}/${id}/edit`, data)
};

export const updateIngredientsApi = (id, data) => {
  return axios.patch(`${Route.updateIngredients}/${id}`, data)
};





// ===================================
// CLIENT DETAIL PAGES APIS 
// ===================================

// PhenoType Analysis Apis
// ------------------------
export const listOfPhenoTypeAnalysisApi = (data) => {
  return axios.get(Route.listOfPhenoTypeAnalysis, { params: data });
};

export const addPhenoTypeAnalysisApi = (id, data) => {
  return axios.patch(`${Route.addPhenoTypeAnalysis}/${id}`, data)
};


// Measurement Record Of Patient Apis
// ------------------------
export const listOfPatientMeasurementRecordApi = (data) => {
  return axios.get(Route.listOfPatientMeasurementRecord, { params: data });
};

export const addPatientMeasurementRecordApi = (data) => {
  return axios.post(Route.addPatientMeasurementRecord, data)
};



// Client (Patient) Apis
// ------------------------
export const listOfClientApi = () => {
  return axios.get(Route.listOfClient);
};

// plans and payments
export const billingPlansApi = () => axios.get(`${Route.billing}/plans`);
export const billingCurrentApi = () => axios.get(`${Route.billing}/current`);
export const billingActiveClientsApi = () => axios.get(`${Route.billing}/active-clients`);
export const billingPlanQuotesApi = (cycle) => axios.get(`${Route.billing}/quotes`, { params: { cycle } });
export const createPlanOrderApi = (planId, cycle) =>
  axios.post(`${Route.billing}/order`, { plan_id: planId, cycle }, { headers: { 'Content-Type': 'application/json' } });
export const verifyPlanPaymentApi = (data) =>
  axios.post(`${Route.billing}/verify`, data, { headers: { 'Content-Type': 'application/json' } });
// "I've paid, check status": ask Razorpay about a pending order (all pending ones without an id)
export const checkPlanPaymentApi = (orderId) =>
  axios.post(`${Route.billing}/check`, orderId ? { order_id: orderId } : {}, { headers: { 'Content-Type': 'application/json' } });
export const myPaymentsApi = () => axios.get(`${Route.billing}/payments`);
// every plan the doctor has had, Free periods included
export const planHistoryApi = () => axios.get(`${Route.billing}/history`);
export const allPaymentsApi = (params) => axios.get(`${Route.billing}/all-payments`, { params });
// admin: a doctor's current plan, and giving them a plan without payment
export const adminDoctorPlanApi = (userId) => axios.get(`${Route.billing}/admin/doctors/${userId}/plan`);
export const adminSetPlanApi = (data) =>
  axios.post(`${Route.billing}/admin/set-plan`, data, { headers: { 'Content-Type': 'application/json' } });
export const chooseFreePlanApi = (keepActive) =>
  axios.post(`${Route.billing}/free`, keepActive ? { keep_active: keepActive } : {}, { headers: { 'Content-Type': 'application/json' } });

// appointments between two dates (YYYY-MM-DD)
export const listOfAppointmentsApi = (from, to) => {
  return axios.get(Route.appointments, { params: { from, to } });
};

export const addAppointmentApi = (data) => {
  return axios.post(Route.appointments, data, { headers: { 'Content-Type': 'application/json' } });
};

// reschedule, change status or notes
export const updateAppointmentApi = (id, data) => {
  return axios.patch(`${Route.appointments}/${id}`, data, { headers: { 'Content-Type': 'application/json' } });
};

export const addClientApi = (data) => {
  return axios.post(Route.addClient, data);
};

export const clientDuplicateCheckApi = (params) => {
  return axios.get(Route.clientDuplicateCheck, { params });
};

export const editClientApi = (id, data) => {
  return axios.get(`${Route.editClient}/${id}/edit`, data)
};

export const updateClientApi = (id, data) => {
  return axios.patch(`${Route.updateClient}/${id}`, data)
};

// send a new verification link to the patient
export const resendClientVerificationApi = (id) => {
  return axios.post(`${Route.updateClient}/${id}/resend-verification`)
};

// Patient Medical Data Apis in Client Detail
// ------------------------
export const listOfMedicalDataApi = (data) => {
  return axios.get(Route.listOfMedicalData, { params: data });
};

export const addMedicalDataApi = (data) => {
  return axios.post(Route.addMedicalData, data);
};

export const editMedicalDataApi = (id) => {
  return axios.get(`${Route.editMedicalData}/${id}/edit`)
};

export const updateMedicalDataApi = (id, data) => {
  return axios.patch(`${Route.updateMedicalData}/${id}`, data)
};

// Patient Routine Diet Data Apis
// ------------------------
export const listOfPatientRoutineDietApi = (data) => {
  return axios.get(Route.listOfPatientRoutineDiet, { params: data });
};

export const addPatientRoutineDietApi = (data) => {
  return axios.post(Route.addPatientRoutineDiet, data);
};

export const editPatientRoutineDietApi = (id, data) => {
  return axios.get(`${Route.editPatientRoutineDiet}/${id}/patient`, data)
};

export const updatePatientRoutineDietApi = (id, data) => {
  return axios.put(`${Route.updatePatientRoutineDiet}/${id}`, data)
};

// Tags Apis in Client Detail
// ------------------------
export const listOfTagApi = (data) => {
  return axios.get(Route.listOfTag, { params: data });
};

export const addTagApi = (data) => {
  return axios.post(Route.addTag, data);
};

export const editTagApi = (id, data) => {
  return axios.get(`${Route.editTag}/${id}/edit`, data)
};

export const updateTagApi = (id, data) => {
  return axios.patch(`${Route.updateTag}/${id}`, data)
};

export const deleteTagApi = (id) => {
  return axios.delete(`${Route.deleteTag}/${id}`)
};

// Assign Herb Apis in Client Detail 
// ------------------------
export const listOfAssignHerbApi = (data) => {
  return axios.get(Route.listOfAssignHerb, { params: data });
};

export const addAssignHerbApi = (data) => {
  return axios.post(Route.addAssignHerb, data);
};

export const updateAssignHerbApi = (id, data) => {
  return axios.patch(`${Route.updateAssignHerb}/${id}`, data)
};


// Assign Diet Page-> Patient Detail form Apis in Client Detail 
// ------------------------
// export const updateMedicalDataApi = (id, data) => {
//   return axios.patch(`${Route.updateMedicalData}/${id}`, data)
// };

// Assign Diet Apis in Client Detail 
// ------------------------
export const listOfAssignDietApi = (data) => {
  return axios.get(Route.listOfAssignDiet, { params: data });
};

export const addAssignDietApi = (data) => {
  return axios.post(Route.addAssignDiet, data);
};

export const editAssignDietApi = (data) => {
  return axios.post(`${Route.editAssignDiet}/edit`, data)
};

// assigned diets over some days, what's not sent yet, send them (email + PDF), and the PDF
export const assignedDietRangeApi = (patientId, from, to) =>
  axios.get(`${Route.assignDiet}/range`, { params: { patient_id: patientId, from, to } });
export const unsentDietDatesApi = (patientId) =>
  axios.get(`${Route.assignDiet}/unsent`, { params: { patient_id: patientId } });
export const sendDietApi = (patientId, dates) =>
  axios.post(`${Route.assignDiet}/send`, { patient_id: patientId, dates }, { headers: { 'Content-Type': 'application/json' } });
export const dietPdfApi = (patientId, from, to) =>
  axios.get(`${Route.assignDiet}/pdf`, { params: { patient_id: patientId, from, to }, responseType: 'blob' });

export const updateAssignDietApi = (data) => {
  return axios.post(Route.updateAssignDiet, data)
};


// Assign Diet page -> ingredient avoided by client Apis in Client Detail 
// ------------------------
export const avoidIngredientByClientApi = (data) => {
  return axios.post(Route.avoidIngredientByClient, data);
};


// Recipe Page Apis 
// ------------------------
export const listOfRecipesApi = (data) => {
  return axios.get(Route.listOfRecipes, data);
};

// whether a recipe name is still free (ignoreId: the recipe being edited)
export const recipeNameAvailableApi = (name, ignoreId) => {
  return axios.get(Route.recipeNameAvailable, { params: { name, ignore_id: ignoreId || undefined } });
};

export const addNewRecipeApi = (data) => {
  return axios.post(Route.addNewRecipe, data);
};

export const editRecipeApi = (id, data) => {
  return axios.get(`${Route.editRecipe}/${id}/edit`, data)
};

export const updateRecipeApi = (id, data) => {
  return axios.patch(`${Route.updateRecipe}/${id}`, data)
};

export const RecipeImageUploadApi = (data) => {
  return axios.post(Route.recipeImageUpload, data);
};


